<script setup lang="ts">
import { ref, watch } from 'vue';
import type { usePdfRenderer } from './use-pdf-renderer';

type Props = {
  /** Renderer instance shared with `PdfViewerControls`. */
  renderer: ReturnType<typeof usePdfRenderer>;
  /** URL of the PDF to render page by page. */
  fileUrl: string;
  /**
   * Whether the canvas box scrolls internally (capped at 80vh). Disable when
   * the host provides its own scroll container (e.g. a modal body) so only a
   * single scrollbar appears.
   */
  scrollable?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  scrollable: true,
});

const canvasRef = ref<HTMLCanvasElement | null>(null);

watch(
  [canvasRef, () => props.fileUrl],
  async ([canvas, url], previous) => {
    if (!canvas || !url) return;
    const [previousCanvas, previousUrl] = previous ?? [];
    if (canvas === previousCanvas && url === previousUrl) return;
    await props.renderer.loadPDF(canvas, url);
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="relative w-full"
    :class="{ 'max-h-[80vh] min-h-[400px] overflow-auto': props.scrollable }"
  >
    <div
      v-if="renderer.isLoading.value"
      class="flex flex-col items-center justify-center gap-lg py-6xl"
    >
      <div
        class="h-3xl w-3xl animate-spin rounded-full border-2 border-line border-t-primary"
      />
    </div>

    <div
      v-else-if="renderer.error.value"
      class="px-2xl py-6xl text-center text-sm text-on-surface-secondary"
    >
      {{ renderer.error.value }}
    </div>

    <canvas
      v-show="!renderer.isLoading.value && !renderer.error.value"
      ref="canvasRef"
      class="mx-auto block h-auto"
    />
  </div>
</template>
